import { createSlice } from '@reduxjs/toolkit';
import type {
  ResolvedFieldMappingItem,
  FieldMappingEntry,
} from '../../services/api/ediFieldMappingService';
import {
  resolveFieldMapping,
  listFieldMappingEntries,
  saveFieldMapping,
} from '../thunks/ediFieldMappingThunks';

// ============================================================================
// EDI Field Mapping slice — Spec mlinkone-buyer-po-field-mapping (group 8).
//
// Holds the resolved effective map (read-only display) + the raw override
// entries for the currently selected scope (editor source-of-truth), plus
// load/save status and the BE validation messages from the last save attempt.
// ============================================================================

export interface EdiFieldMappingState {
  /** Resolved effective map (baseline + overrides), each property annotated. */
  resolved: ResolvedFieldMappingItem[];
  /** Raw override entries authored at the selected scope. */
  entries: FieldMappingEntry[];
  /** scope_path the BE reported for the selected scope (informational). */
  scopePath: string;
  resolveStatus: 'idle' | 'loading' | 'success' | 'failure';
  entriesStatus: 'idle' | 'loading' | 'success' | 'failure';
  saveStatus: 'idle' | 'saving' | 'success' | 'failure';
  /** Last load error (resolve/list). */
  loadError: string | null;
  /** BE validation messages from the last save (rendered inline by the page). */
  saveErrors: string[];
}

const initialState: EdiFieldMappingState = {
  resolved: [],
  entries: [],
  scopePath: '',
  resolveStatus: 'idle',
  entriesStatus: 'idle',
  saveStatus: 'idle',
  loadError: null,
  saveErrors: [],
};

const ediFieldMappingSlice = createSlice({
  name: 'ediFieldMapping',
  initialState,
  reducers: {
    clearSaveErrors: (state) => { state.saveErrors = []; },
    resetFieldMapping: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(resolveFieldMapping.pending, (state) => {
        state.resolveStatus = 'loading';
        state.loadError = null;
      })
      .addCase(resolveFieldMapping.fulfilled, (state, action) => {
        state.resolveStatus = 'success';
        state.resolved = action.payload.items;
      })
      .addCase(resolveFieldMapping.rejected, (state, action) => {
        state.resolveStatus = 'failure';
        state.loadError = (action.payload as string) ?? action.error.message ?? 'Failed to resolve field mapping';
      });

    builder
      .addCase(listFieldMappingEntries.pending, (state) => {
        state.entriesStatus = 'loading';
        state.loadError = null;
      })
      .addCase(listFieldMappingEntries.fulfilled, (state, action) => {
        state.entriesStatus = 'success';
        state.entries = action.payload.entries;
        state.scopePath = action.payload.scopePath;
      })
      .addCase(listFieldMappingEntries.rejected, (state, action) => {
        state.entriesStatus = 'failure';
        state.loadError = (action.payload as string) ?? action.error.message ?? 'Failed to list field-mapping entries';
      });

    builder
      .addCase(saveFieldMapping.pending, (state) => {
        state.saveStatus = 'saving';
        state.saveErrors = [];
      })
      .addCase(saveFieldMapping.fulfilled, (state, action) => {
        state.saveStatus = 'success';
        state.scopePath = action.payload.scopePath;
      })
      .addCase(saveFieldMapping.rejected, (state, action) => {
        state.saveStatus = 'failure';
        state.saveErrors = (action.payload as string[]) ?? [action.error.message ?? 'Failed to save field mapping'];
      });
  },
});

export const { clearSaveErrors, resetFieldMapping } = ediFieldMappingSlice.actions;
export default ediFieldMappingSlice.reducer;
